// ISTQuestionPage.jsx
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton,
  Snackbar,
  Typography,
} from '@mui/material'
import { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import MultiQuestion from '../../components/questions/MultiQuestion'
import EnumQuestion from '../../components/questions/EnumQuestion'
import MultipleABQuestion from '../../components/questions/MultipleABQuestion'
import EssayLength from '../../components/questions/EssayLength'
import EssayFreeText from '../../components/questions/EssayFreeText'
import { BaseService } from '../../services/BaseService'
import { useQuestionDispatch } from '../../context/QuestionContext/QuestionContext'
import { useModule } from '../../context/ModuleContext/ModuleContext'
import { useGetUser } from '../../hooks/useGetUser'
import { ADD_QUESTIONS_LIST } from '../../reducers/reducesTypes'
import { moduleTypeIncludes, modulesType } from '../../constants/modules'
import WizardIST from '../../components/questions/WizardIST'
import SoalOnly from '../../components/questions/SoalOnly'
import DescriptionOnly from '../../components/questions/DescriptionOnly'
import { useCameraContext } from '../../context/CameraContext/CameraContext'
import { useGeolocationContext } from '../../context/GeolocationContext/GeolocationContext'
import { getCurrentPosition } from '../../utils/geolocation'
import { calculateSessionTime } from '../../utils/calculateSessionTime'
import useAntiFocus from '../../hooks/useAntiFocus'
import { useLogging } from '../../context/LoggingContext/LoggingContext'
import useAntiScreenshot from '../../hooks/useAntiScreenshot'
import { getAudioContext, playWarningBeep } from '../../context/AudioContext/AudioContext'
import { clearISTCameraSession } from '../../utils/clearISTCameraSession'
import { removeLocalStorageData } from '../../utils/storage/storage'
import ProctorOverlay from '../../components/Loader/ProctorOverlay'
import DobModal from '../../components/Modal/DobModal'
import { extractDob, isDobValid } from '../../utils/dobValidation'

// === API Calls ===
async function fetchHistory(userId) {
  const service = new BaseService('/m_history_2')
  const res = await service.getHistory({ id_user: userId })
  return res.data
}
async function fetchSoalPertype(subType = 'SE', id) {
  const service = new BaseService('/m_soal_pertype')
  const res = await service.getQuestion({ id_modul: id, sub_type: subType })
  return res.data
}
async function fetchSoalSubtype(id) {
  const service = new BaseService('/m_sub_type')
  const res = await service.getQuestion({ id_modul: id })
  return res.data
}
// async function fetchSoalME(id) {
//   const service = new BaseService('/m_gambar_soal')
//   const res = await service.getQuestion({ sub_type: 'ME', id_modul: id })
//   return res.data
// }

const ISTQuestionPage = () => {
  const { id } = useParams()
  const { user } = useGetUser()
  const dispatch = useQuestionDispatch()
  const { modules } = useModule()
  const {
    permissionGranted: cameraGranted,
    // startCapture,
    stopCapture,
    setModuleId,
    startCameraSession,
    requestCameraPermission,
  } = useCameraContext()
  const { permissionGranted: locationGranted } = useGeolocationContext()
  const [isCameraBlocked, setIsCameraBlocked] = useState(false)
  const [isLocationBlocked, setIsLocationBlocked] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const { addLog } = useLogging()
  const { ScreenshotDialog } = useAntiScreenshot({
    userId: user.id,
    moduleId: id,
    addLog,
  })
  const isBeforeUnloadRef = useRef(false)
  const cameraStartedRef = useRef(false)

  const [subtypes, setSubtypes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isPreloadingImages, setIsPreloadingImages] = useState(false)
  const [currentSubtypeIndex, setCurrentSubtypeIndex] = useState(0)
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)

  const [openDialog, setOpenDialog] = useState(false)
  const [alreadyDoneDialog, setAlreadyDoneDialog] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [modalContent, setModalContent] = useState('')
  const [freeze, setFreeze] = useState(false)
  const [isEnded, setIsEnded] = useState(false)

  const [isFinalLoading, setIsFinalLoading] = useState(false)
  const [isFinalError, setIsFinalError] = useState(false)
  const [isSubtestTransitioning, setIsSubtestTransitioning] = useState(false)

  const isSubmittingRef = useRef(false)

  useAntiFocus({
    onBlur: () => {
      if (isEnded) return
      if (isBeforeUnloadRef.current) return

      playWarningBeep()
      addLog(user.id, id, 'Berpindah/Menyembunyikan Tab')
    },
    onHidden: () => {
      if (isEnded) return
      if (isBeforeUnloadRef.current) return

      playWarningBeep()
      addLog(user.id, id, 'Berpindah/Menyembunyikan Tab')
    },
  })

  // START: audio warning
  useEffect(() => {
    const shouldUnlock = localStorage.getItem('audioUnlockedIntent') === 'true'
    if (!shouldUnlock) return

    const ctx = getAudioContext()

    // 🔑 unlock via user gesture minimal
    const unlock = () => {
      if (ctx.state === 'suspended') {
        ctx.resume()
      }
      document.removeEventListener('click', unlock)
      document.removeEventListener('keydown', unlock)
    }

    document.addEventListener('click', unlock)
    document.addEventListener('keydown', unlock)

    return () => {
      document.removeEventListener('click', unlock)
      document.removeEventListener('keydown', unlock)
    }
  }, [])
  // END: audio warning

  // START: set flag before unload
  useEffect(() => {
    if (isEnded) {
      window.onbeforeunload = null
      return
    }

    const warnBeforeUnload = (e) => {
      isBeforeUnloadRef.current = true
      e.preventDefault()
      e.returnValue = 'Leave Page?'
    }

    window.addEventListener('beforeunload', warnBeforeUnload)
    window.onbeforeunload = warnBeforeUnload

    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload)
      window.onbeforeunload = null
    }
  }, [isEnded])

  useEffect(() => {
    const reset = () => {
      // browser gak jadi unload → user klik "Cancel"
      isBeforeUnloadRef.current = false
    }

    window.addEventListener('focus', reset)
    return () => window.removeEventListener('focus', reset)
  }, [])
  // END: set flag before unload

  const rules = useMemo(() => {
    try {
      return typeof modules?.rules === 'string' ? JSON.parse(modules.rules) : modules?.rules || {}
    } catch {
      return {}
    }
  }, [modules?.rules])

  // helper: decide start subtype index based on localStorage
  function getStartIndexFromStorage(subtypesList) {
    try {
      const raw = localStorage.getItem('questionIst')
      if (!raw) return 0
      const parsed = JSON.parse(raw)
      // if any subType still inProgress, return its index
      const inProgress = parsed.data?.find((d) => d.data?.status === 'inProgress')
      if (inProgress) {
        return subtypesList.findIndex((s) => s.type_code === inProgress.subType)
      }
      // otherwise pick first subtype not present in storage (first undone)
      const doneTypes = (parsed.data || []).map((d) => d.subType)
      const firstUndone = subtypesList.findIndex((s) => !doneTypes.includes(s.type_code))
      return firstUndone === -1 ? 0 : firstUndone
    } catch (e) {
      return 0
    }
  }

  useEffect(() => {
    if (rules?.camera !== 1) return
    if (!id) return

    requestCameraPermission() // 🚨 INI YANG BIKIN cameraGranted JADI TRUE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules?.camera, id])

  // START: access camera and location
  // blurred when camera and location is not granted
  // useEffect(() => {
  //   if (cameraGranted === false || locationGranted === false) {
  //     setIsBlocked(true)
  //   } else if (cameraGranted === true && locationGranted === true) {
  //     setIsBlocked(false)
  //   }
  // }, [cameraGranted, locationGranted])
  useEffect(() => {
    const needCamera = rules?.camera !== 0
    const needGeo = rules?.geo !== 0

    if ((needCamera && cameraGranted === false)) {
      setIsCameraBlocked(true)
    } else {
      setIsCameraBlocked(false)
    }

    if (needGeo && locationGranted === false) {
      setIsLocationBlocked(true)
    } else {
      setIsLocationBlocked(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraGranted, locationGranted, modules?.rules])

  // START: init session camera, new mechanism
  useEffect(() => {
    // if (rules?.camera === false) return
    if (rules?.camera !== 1) return
    if (!cameraGranted) return
    if (!id) return
    if (cameraStartedRef.current) return

    cameraStartedRef.current = true
    startCameraSession()

    return () => {
      stopCapture()
      cameraStartedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraGranted, id, modules?.rules])
  // END: init session camera, new mechanism

  useEffect(() => {
    if (rules?.geo !== 0) {
      getCurrentPosition({ timeout: 15000 })
        .then((loc) => {
          localStorage.setItem('geoLoc', JSON.stringify(loc))
        })
        .catch(() => {})
    }
  }, [rules?.geo])

  useEffect(() => {
    if (id) {
      setModuleId(Number(id)) // kasih tau camera modul mana
    }
  }, [id, setModuleId])
  // END: access camera and location

  useEffect(() => {
    fetchSoalSubtype(id).then((res) => {
      let subtypesData = []

      res.data.forEach((s) => {
        // inject DESCRIPTION dulu
        const descSubtype = {
          type_sub_modul: 'DESCRIPTION ONLY',
          type_sub_modul_original: s?.type_sub_modul,
          type_name: s?.type_name,
          id_modul: id,
          type_code: `${s?.type_code}_DESC_IST`, // kasih suffix biar unik
          type_code_original: s?.type_code,
          id: String(Date.now() + Math.random()),
          deskripsi: s?.deskripsi || '-', // masukin deskripsi dari API
          jumlah_soal: s?.jumlah_soal,
          waktu: s?.waktu,
          freeze_time: true,
        }
        subtypesData.push(descSubtype)

        // lalu push subtype aslinya
        subtypesData.push(s)

        // khusus ME, tambahin ME_IMAGE sebelum ME asli
        if (s.type_code === 'ME') {
          const meImageSubtype = {
            type_sub_modul: 'SOAL ONLY',
            type_name: 'SOAL ME',
            id_modul: id,
            type_code: 'ME_IMAGE',
            waktu: '3',
            id: String(Date.now() + Math.random()),
          }
          // inject ME_IMAGE sebelum ME
          subtypesData.splice(subtypesData?.length - 1, 0, meImageSubtype)
        }
      })

      setSubtypes(subtypesData)
      setLoading(false)

      const start = getStartIndexFromStorage(subtypesData)
      setCurrentSubtypeIndex(start)
    })
  }, [id])

  useEffect(() => {
    if (subtypes?.length > 0) {
      const subtype = subtypes[currentSubtypeIndex]
      setIsLoading(true)
      if (subtype.type_code === 'ME_IMAGE') {
        // fetchSoalME(id)
        //   .then((res) => {
        //     setQuestions(res.data)
        //     dispatch({ type: ADD_QUESTIONS_LIST, list: res.data })
        //   })
        //   .finally(() => setIsLoading(false))
        setQuestions([
          {
            // gambar: res.data[0]?.gambar,
            type_sub_modul: 'SOAL ONLY',
            desc: 'Hafalkan kata-kata di bawah ini selama 3 menit',
            soal_1: 'BUNGA: SOKA, LARAT, FLAMBOYAN, YASMIN, DAHLIA',
            soal_2: 'PERKAKAS: WAJAN, JARUM, KIKIR, CANGKUL, PALU',
            soal_3: 'BURUNG: ITIK, ELANG, WALET, TEKUKUR, NURI',
            soal_4: 'KESENIAN: QUINTET, ARCA, OPERA, UKIRAN, GAMELAN',
            soal_5: 'BINATANG: RUSA, MUSANG, BERUANG, HARIMAU, ZEBRA',
          },
        ])
        dispatch({
          type: ADD_QUESTIONS_LIST,
          list: [
            {
              // gambar: res.data[0]?.gambar,
              type_sub_modul: 'SOAL ONLY',
              desc: 'Hafalkan kata-kata di bawah ini selama 3 menit',
              soal_1: 'BUNGA: SOKA, LARAT, FLAMBOYAN, YASMIN, DAHLIA',
              soal_2: 'PERKAKAS: WAJAN, JARUM, KIKIR, CANGKUL, PALU',
              soal_3: 'BURUNG: ITIK, ELANG, WALET, TEKUKUR, NURI',
              soal_4: 'KESENIAN: QUINTET, ARCA, OPERA, UKIRAN, GAMELAN',
              soal_5: 'BINATANG: RUSA, MUSANG, BERUANG, HARIMAU, ZEBRA',
            },
          ],
        })
        setIsLoading(false)
      } else if (subtype.type_code.toLowerCase().toLowerCase().includes('_desc_ist')) {
        setQuestions([
          {
            type_sub_modul: 'DESCRIPTION ONLY',
            deskripsi: subtype?.deskripsi || '-',
          },
        ])
        dispatch({
          type: ADD_QUESTIONS_LIST,
          list: [
            {
              type_sub_modul: 'DESCRIPTION ONLY',
              deskripsi: subtype?.deskripsi || '-',
            },
          ],
        })
        setIsLoading(false)
      } else {
        fetchSoalPertype(subtype.type_code, id)
          .then(async (res) => {
            const imageUrls = []
            res.data.forEach((q) => {
              if (q.link_gambar && q.link_gambar.trim() !== '') imageUrls.push(q.link_gambar)
              if (q.gambar && q.gambar.trim() !== '') imageUrls.push(q.gambar)
              const opts = [q.pilihan_1, q.pilihan_2, q.pilihan_3, q.pilihan_4, q.pilihan_5]
              opts.forEach((opt) => {
                if (
                  opt &&
                  typeof opt === 'string' &&
                  (opt.startsWith('http') || opt.startsWith('data:image'))
                ) {
                  imageUrls.push(opt)
                }
              })
            })

            if (imageUrls.length > 0) {
              setIsPreloadingImages(true)
              const loadImagesPromise = Promise.all(
                imageUrls.map((url) => {
                  return new Promise((resolve) => {
                    const img = new Image()
                    img.src = url
                    img.onload = resolve
                    img.onerror = resolve
                  })
                }),
              )

              // Safeguard timeout: maksimal 15 detik loading gambar
              const timeoutPromise = new Promise((resolve) => {
                setTimeout(() => resolve('timeout'), 15000)
              })

              await Promise.race([loadImagesPromise, timeoutPromise])
              setIsPreloadingImages(false)
            }

            setQuestions(res.data)
            dispatch({ type: ADD_QUESTIONS_LIST, list: res.data })
          })
          .finally(() => setIsLoading(false))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtypes, currentSubtypeIndex, id])

  // function handleFinishSubtype(answers) {
  //   // pindah ke subtype berikutnya
  //   if (currentSubtypeIndex < subtypes.length - 1) {
  //     setCurrentSubtypeIndex((prev) => prev + 1)
  //   } else {
  //     navigate('/') // selesai semua
  //   }
  // }

  // function handleFinishSubtype() {
  //   if (currentSubtypeIndex < subtypes?.length - 1) {
  //     setCurrentSubtypeIndex((prev) => prev + 1) // lanjut ke subtype berikutnya
  //   } else {
  //     handleAllFinish() // baru submit semua subtest
  //     setIsOpen(true)
  //     // navigate('/')
  //   }
  // }
  async function handleFinishSubtype() {
    if (isEnded) return
    if (isSubmittingRef.current) return

    if (currentSubtypeIndex < subtypes.length - 1) {
      setIsSubtestTransitioning(true)
      await new Promise((resolve) => setTimeout(resolve, 800))
      setCurrentSubtypeIndex((prev) => prev + 1)
      setIsSubtestTransitioning(false)
    } else {
      await handleAllFinish()
    }
  }

  // taro di ISTQuestionPage
  async function submitAllSubtests(user, id_modul) {
    const raw = localStorage.getItem('questionIst')
    if (!raw) return

    const parsed = JSON.parse(raw)
    const subTypesToSkip = ['ME_IMAGE', '_DESC_IST']

    const answers = await Promise.all(
      parsed.data
        .filter(
          (entry) =>
            !subTypesToSkip.some((s) => entry.subType?.toLowerCase().includes(s.toLowerCase())),
        )
        .map(async (entry) => {
          let timeStart = entry?.data?.time_start
          let timeEnd = entry?.data?.time_end
          let duration = entry?.data?.durasi

          // 🛡️ ANTI-OMPONG SHIELD: Jika timeEnd atau duration belum ada/hilang, lengkapi saat itu juga
          if (!timeEnd || !duration) {
            const fallback = calculateSessionTime(entry?.data)
            timeStart = timeStart || fallback.time_start
            timeEnd = timeEnd || fallback.time_end
            duration = duration || fallback.durasi
          }

          return {
            type_soal: entry?.subType,
            answer: transformKeys(entry?.data.questions),
            timeStart,
            timeEnd,
            duration,
          }
        }),
    )

    const { time_start, time_end, durasi } = calculateSessionTime(
      JSON.parse(localStorage.getItem('questionIst')),
    )

    let location = null

    // if (rules?.geo !== 0) {
    //   try {
    //     location = await getCurrentPosition({ timeout: 7000 })
    //   } catch (err) {
    //     console.warn('Gagal ambil lokasi:', err.message)
    //   }
    // }

    if (rules?.geo !== 0) {
      try {
        location = await getCurrentPosition({ timeout: 15000 })
      } catch (err) {
        const cached = localStorage.getItem('geoLoc')
        if (cached) {
          location = JSON.parse(cached)
        } else {
          console.warn('Gagal ambil lokasi:', err.message)
        }
      }
    }

    const payload = {
      id_user: user.id,
      id_modul,
      tipe_modul: modules?.tipe_modul,
      json_jawaban: JSON.stringify(answers),
      latitude: location?.lat || null,
      longitude: location?.lon || null,
      time_Start: time_start,
      time_End: time_end,
      durasi,
    }

    const service = new BaseService('/m_activity_2')
    const res = await service.postAnswer(payload)

    return res
  }

  // const handleAllFinish = async () => {
  //   try {
  //     const res = await submitAllSubtests(user, id, subtype)
  //     setModalContent(res?.data?.msg)
  //     clearISTCameraSession()
  //     localStorage.removeItem('questionIst')

  //     // ❄️ Freeze timer biar gak jalan lagi
  //     setFreeze(true)

  //     // Tandai ujian selesai
  //     setIsEnded(true)

  //     // Stop kamera & tracking
  //     stopCapture()

  //     addLog(user.id, id, 'Selesai mengerjakan test')
  //     // console.log('All subtests submitted!')
  //   } catch (e) {
  //     console.error('Submit all failed', e)
  //   }
  // }

  const handleAllFinish = async () => {
    if (isSubmittingRef.current) return

    setIsOpen(true)
    setIsFinalLoading(true)
    setIsFinalError(false)
    isSubmittingRef.current = true

    try {
      // Graceful delay 500ms agar dialog sempat me-render status loading dengan mulus
      await new Promise((resolve) => setTimeout(resolve, 500))

      // freeze SEBELUM async
      setFreeze(true)
      setIsEnded(true)

      const res = await submitAllSubtests(user, id, subtype)
      setIsFinalLoading(false)
      setModalContent(res?.data?.msg)
      clearISTCameraSession()
      localStorage.removeItem('questionIst')
      stopCapture()

      // 🔒 log juga kena lock ini
      await addLog(user.id, id, 'Selesai mengerjakan test')
    } catch (e) {
      console.error('Submit all failed', e)
      setIsFinalError(true)
      setIsFinalLoading(false)
      isSubmittingRef.current = false // rollback kalau gagal
      setModalContent('Gagal mengirim jawaban. Silahkan periksa koneksi internet Anda.')
    }
  }

  // === Queries ===
  const { data: dataHistory } = useQuery(['rules-history', id], () => fetchHistory(user.id))

  const [showDobModal, setShowDobModal] = useState(false)
  const [currentDob, setCurrentDob] = useState(null)
  const [dobAlert, setDobAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const { data: dataDob, refetch: refetchDob } = useQuery(
    ['check-dob-ist-page', user?.id],
    async () => {
      const service = new BaseService('/m_dob')
      return await service.getDob({ id_user: user?.id })
    },
    {
      enabled: !!user?.id,
    },
  )

  useEffect(() => {
    if (dataDob !== undefined) {
      const extracted = extractDob(dataDob)
      setCurrentDob(extracted)
      if (!isDobValid(extracted)) {
        setShowDobModal(true)
      } else {
        setShowDobModal(false)
      }
    }
  }, [dataDob])

  const checkDoneTask = dataHistory?.data?.some((h) => h.id === id)

  // === Navigation Guard (Back Button) ===
  const handlePopstate = useCallback(
    (event) => {
      if (event.state && modules?.startQuestion) {
        navigate(location.pathname, { replace: true })
      }
    },
    [modules?.startQuestion, navigate, location.pathname],
  )

  useEffect(() => {
    window.addEventListener('popstate', handlePopstate)
    return () => window.removeEventListener('popstate', handlePopstate)
  }, [handlePopstate])



  function renderQuestion(moduleType, question, idx) {
    switch (moduleType) {
      case modulesType.MULTIPLE:
        return <MultiQuestion key={idx} dataQuestion={question} indices={idx + 1} />
      case modulesType.ENUM:
        return <EnumQuestion key={idx} dataQuestion={question} indices={idx + 1} />
      case modulesType.MULTIPLE_AB:
        return <MultipleABQuestion key={idx} dataQuestion={question} indices={idx + 1} />
      case modulesType.ESSAY_LENGTH:
        return <EssayLength key={idx} dataQuestion={question} indices={idx + 1} />
      case modulesType.ESAI_FREE_TEXT:
        return (
          <EssayFreeText
            key={idx}
            dataQuestion={question}
            indices={idx + 1}
            typeSoal={
              subtypes[currentSubtypeIndex]?.type_code === 'ZR' ||
              subtypes[currentSubtypeIndex]?.type_code === 'RA'
                ? 'number'
                : 'text'
            }
          />
        )
      case modulesType.SOAL_ONLY:
        return <SoalOnly key={idx} dataQuestion={question} indices={idx + 1} />
      case modulesType.DESCRIPTION_ONLY:
        return <DescriptionOnly key={idx} dataQuestion={subtype} />
      default:
        return (
          <Box key={idx}>
            <Typography>
              Oops! <br /> Maaf soal belum ada, stay tuned 😀
            </Typography>
          </Box>
        )
    }
  }

  // function normalizeAnswers(values, subtype, questions) {
  //   if (!['WU', 'FA'].includes(subtype?.type_code)) return values

  //   const letterMap = ['a', 'b', 'c', 'd', 'e']
  //   const newValues = {}

  //   Object.entries(values).forEach(([key, val]) => {
  //     if (!val) {
  //       newValues[key] = val
  //       return
  //     }

  //     const qIndex = parseInt(key.replace('question_', ''), 10) - 1
  //     const q = questions[qIndex]
  //     const options = [q?.pilihan_1, q?.pilihan_2, q?.pilihan_3, q?.pilihan_4, q?.pilihan_5]

  //     const optIndex = options.findIndex((opt) => opt === val)
  //     newValues[key] = optIndex >= 0 ? letterMap[optIndex] : val
  //   })

  //   return newValues
  // }

  function transformKeys(values) {
    const newValues = {}
    Object.entries(values).forEach(([key, val]) => {
      // hapus prefix "question_"
      const newKey = key.replace(/^question_/, '')
      newValues[newKey] = val
    })
    return newValues
  }

  function handleData(moduleType, questionsList) {
    if (!questionsList) return []
    let filtered = questionsList
    if (moduleType === modulesType.MULTIPLE) {
      filtered = questionsList.filter(
        (q) => q.pilihan_1 || q.pilihan_2 || q.pilihan_3 || q.pilihan_4,
      )
    }
    return filtered.map((q, idx) => renderQuestion(moduleType, q, idx))
  }

  if (!moduleTypeIncludes.includes(modules.tipe_modul)) {
    return (
      <Box
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Typography fontWeight={700}>Oops!</Typography>
        <Typography mt={1}>Maaf soal ini belum tersedia. Hubungi Dika Psikotes 😀</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 3, fontWeight: 700, color: 'white' }}
        >
          Kembali
        </Button>
      </Box>
    )
  }

  if (checkDoneTask) {
    return (
      <Dialog open={alreadyDoneDialog}>
        <DialogContent>Module sudah pernah dikerjakan.</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setAlreadyDoneDialog(false)
              removeLocalStorageData('module')
              removeLocalStorageData('questionIst')
              removeLocalStorageData('camera_capture_log')
              // navigate('/', { replace: true })
              window.location.href = (process.env.REACT_APP_BASE_NAME || '') + '/'
            }}
            sx={{ backgroundColor: ({ palette }) => palette.warning.light, color: 'white' }}
          >
            Kembali
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (loading) return <Skeleton variant="rectangular" width="100%" height={200} />

  const subtype = subtypes[currentSubtypeIndex]

  return (
    <Box height="100%" width="100%">
      {!isLoading ? (
        <WizardIST
          key={subtype.type_code}
          questions={questions}
          id_modul={id}
          subtype={subtype}
          onFinish={handleFinishSubtype}
          freeze={freeze}
        >
          {handleData(subtype.type_sub_modul, questions)}
        </WizardIST>
      ) : (
        [...Array(5)].map((_, idx) => (
          <Skeleton
            key={idx}
            height={idx === 0 ? '250px' : '80px'}
            width="100%"
            sx={{ transform: 'scale(1,.8)' }}
          />
        ))
      )}

      <Dialog open={openDialog}>
        <DialogContent>Sesi masih berlangsung, harap selesaikan terlebih dahulu</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setOpenDialog(false)}
            sx={{ backgroundColor: ({ palette }) => palette.warning.light, color: 'white' }}
          >
            Lanjutkan
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isSubtestTransitioning}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 999,
          flexDirection: 'column',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          textAlign: 'center',
          px: 3,
        }}
      >
        <CircularProgress color="inherit" size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" fontWeight="bold">
          Menyimpan Jawaban & Menyiapkan Subtes Berikutnya...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
          Sistem sedang mengkalkulasi sesi. Mohon jangan me-refresh atau menutup browser Anda.
        </Typography>
      </Backdrop>

      <Dialog
        open={isOpen}
        // User tidak bisa tutup modal sembarangan lewat backdrop/escape
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return
          setIsOpen(false)
        }}
      >
        <DialogContent sx={{ textAlign: 'center', minWidth: '300px' }}>
          {isFinalLoading && (
            <Box sx={{ py: 2 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography fontWeight="bold">Sedang memproses & mengirim seluruh hasil test...</Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                Sistem sedang mengkalkulasi sesi. Mohon jangan tutup atau refresh halaman ini.
              </Typography>
            </Box>
          )}

          {isFinalError && (
            <Box sx={{ py: 2 }}>
              <Typography color="error" fontWeight="bold">
                Ups! Pengiriman Gagal
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {modalContent}
              </Typography>
              <Button variant="contained" fullWidth onClick={handleAllFinish} sx={{ mt: 3 }}>
                Kirim Ulang Jawaban
              </Button>
            </Box>
          )}

          {!isFinalLoading && !isFinalError && (
            <Box sx={{ py: 1 }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                Berhasil!
              </Typography>
              <Typography>{modalContent}</Typography>
              <Typography sx={{ mt: 2, fontWeight: 500 }}>
                Silahkan klik beranda untuk kembali ke halaman utama
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {/* Tombol Beranda HANYA muncul jika tidak loading dan tidak error */}
          {!isFinalLoading && !isFinalError && (
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setIsOpen(false)
                removeLocalStorageData('module')
                removeLocalStorageData('questionIst')
                removeLocalStorageData('camera_capture_log')
                removeLocalStorageData('geoLoc')
                window.location.href = (process.env.REACT_APP_BASE_NAME || '') + '/'
              }}
              sx={{ backgroundColor: ({ palette }) => palette.success.light, color: 'white' }}
            >
              Beranda
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ProctorOverlay
        open={isCameraBlocked}
        title="⚠️ Kamera nonaktif"
      />

      <ProctorOverlay
        open={isLocationBlocked}
        title="⚠️ Lokasi nonaktif"
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column' }}
        open={isPreloadingImages}
      >
        <CircularProgress color="inherit" />
        <Typography mt={2} fontWeight={500}>
          Sedang menyiapkan soal dan gambar, mohon tunggu...
        </Typography>
      </Backdrop>
      <ScreenshotDialog />

      <DobModal
        open={showDobModal}
        userId={user?.id}
        initialDob={currentDob}
        onSuccess={(newDob, msg) => {
          setCurrentDob(newDob)
          setShowDobModal(false)
          setDobAlert({
            open: true,
            message: msg || 'Tanggal lahir berhasil disimpan!',
            severity: 'success',
          })
          refetchDob()
        }}
      />

      <Snackbar
        open={dobAlert.open}
        autoHideDuration={4000}
        onClose={() => setDobAlert((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 99999 }}
      >
        <Alert
          onClose={() => setDobAlert((prev) => ({ ...prev, open: false }))}
          severity={dobAlert.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', boxShadow: 3, fontWeight: 600 }}
        >
          {dobAlert.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ISTQuestionPage
